import { useAgentClient } from '@/data/Agents/useAgentClient'
import { watchProject } from '@/data/Agents/watchProject'
import type { Eventually } from '@/data/clientUtils'
import { useEntityQuery } from '@/data/Entities/useEntityQuery'
import { useFieldErrorCountQuery } from '@/data/Hubs/useFieldErrorCountQuery'
import { useHubClient } from '@/data/Hubs/useHubClient'
import { wsTopic } from '@/data/websocketTopics'
import type { DefinedField } from '@/modules/Project/Fields/types'
import { getHubFields } from '@/modules/Workspaces/KnowledgeHub/Hubs/getHubFields'
import { useWebSocketTopic } from '@/sharedComposables/useWebSocketTopic'
import { useDebounceFn } from '@vueuse/core'
import { computed, toRef, type Ref } from 'vue'

export function useHubIndexing(props: { workspaceId: Ref<string>; hubId: Eventually<string> }) {
  const { fetch: fetchHub } = useHubClient({ workspaceId: props.workspaceId })
  const { data: hub } = fetchHub(props.hubId)

  watchProject({ projectId: () => hub.value?.id })

  const { data: entity, isFetched: isEntityFetched } = useEntityQuery({
    workspaceId: props.workspaceId,
    projectId: () => hub.value?.id,
    entityId: () => hub.value?.index?.entityId,
    staleTime: 5_000,
  })

  const agentClient = useAgentClient({ workspaceId: toRef(props, 'workspaceId') })
  const { data: fileProject } = agentClient.fetch(() => hub.value?.fileProjectId)
  const { data: hubProject } = agentClient.fetch(() => hub.value?.id)

  const {
    data: failedFieldCount,
    refetch,
    isFetched: isFailedCountFetched,
  } = useFieldErrorCountQuery({ fileProject })

  /** We may have a bulk of WS events incoming, so let's not spam the API */
  const recountFailedFields = useDebounceFn(refetch, 500)

  const topic = computed(() => wsTopic.project(hub.value?.fileProjectId ?? null))
  const { subscribe } = useWebSocketTopic(topic)
  subscribe('entity:created', () => recountFailedFields())
  subscribe('entity:deleted', () => recountFailedFields())
  subscribe('entity:updated', () => recountFailedFields())
  subscribe('field:updated', () => recountFailedFields())
  subscribe('entity:bulk_deleted', () => recountFailedFields())

  /** States that we interpret as non-computing */
  const stableStates: DefinedField['status'][] = ['complete', 'idle', 'error']

  const hubFields = computed(() => {
    if (!hubProject.value || !entity.value) return
    return getHubFields({
      properties: hubProject.value.properties,
      entity: entity.value,
    })
  })

  /**
   * Indicates whether any field is currently processing.
   * True if any field has a non-stable status (not complete, idle, or error) or unknown staleness.
   */
  const isProcessing = computed(() => {
    if (!hubFields.value) return false

    const { index, hubSummary } = hubFields.value
    for (const field of [index, hubSummary]) {
      if (!stableStates.includes(field.status)) {
        return true
      }
    }

    if (index.staleness.status === 'unknown') {
      const reason = index.staleness.reason
      if (reason === 'no_inputs') {
        // No files in the collection yet
        return false
      } else if (reason === 'inputs_incomplete') {
        // Files in the collection are still processing
        return true
      }
    }

    return false
  })

  /**
   * Indicates whether any field is actively indexing.
   */
  const isIndexing = computed(() => {
    if (!hubFields.value) return false

    const { index, hubSummary } = hubFields.value

    // There's a period when index is computed, and hubSummary hasn't yet started computing.
    // One is complete, and another is idle. We would show "Ready".
    // Let's try to prevent that flickering status and show a fake "indexing state"
    if (
      index.status === 'complete' &&
      index.staleness.status === 'up_to_date' &&
      hubSummary.staleness.status === 'unknown' &&
      hubSummary.staleness.reason === 'inputs_incomplete'
    ) {
      return true
    }

    return index.status === 'computing' || hubSummary.status === 'computing'
  })

  /**
   * Indicates whether any field is stale.
   * True if any field's staleness status is not 'up_to_date'.
   */
  const isStale = computed(() => {
    const fields = Array.from(entity.value?.fields.values() ?? [])
    return fields.some(({ staleness }) => staleness.status === 'stale')
  })

  const issueCount = computed(() => {
    return failedFieldCount.value ?? 0
  })

  const isStateLoaded = computed(() => {
    return (
      isFailedCountFetched.value && isEntityFetched.value && hubProject.value && fileProject.value
    )
  })

  return { isStale, isProcessing, isIndexing, issueCount, isStateLoaded }
}
